import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  useTheme,
} from "@mui/material"
import type { ChatAnalyze } from "../interfaces"

interface FraudScoreProps {
  stats: ChatAnalyze.Stats
}

// Кастомный SVG-gauge (дуга)
function GaugeChart({ value, color }: { value: number; color: string }) {
  const radius = 60
  const strokeWidth = 12
  const cx = 80
  const cy = 80
  // Рисуем только верхнюю полуокружность (180°)
  const circumference = Math.PI * radius
  const pct = Math.min(value / 100, 1)
  const dashOffset = circumference * (1 - pct)

  const bgColor = "rgba(128,128,128,0.15)"

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <svg
        width={160}
        height={100}
        viewBox="0 0 160 100"
        aria-label={`Fraud Score: ${value}`}
      >
        {/* Фоновая дуга */}
        <path
          d={`M ${cx - radius},${cy} A ${radius},${radius} 0 0 1 ${cx + radius},${cy}`}
          fill="none"
          stroke={bgColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Заполненная дуга */}
        <path
          d={`M ${cx - radius},${cy} A ${radius},${radius} 0 0 1 ${cx + radius},${cy}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={`${dashOffset}`}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
        {/* Центральный текст */}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontSize={28}
          fontWeight={800}
          fill={color}
        >
          {value}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          fontSize={10}
          fill="currentColor"
          opacity={0.6}
        >
          из 100
        </text>
      </svg>
    </Box>
  )
}

export function FraudScore({ stats }: FraudScoreProps) {
  const theme = useTheme()
  const { fraudScore, fraudFlags, verdict } = stats

  const gaugeColor =
    fraudScore < 20
      ? theme.palette.success.main
      : fraudScore < 50
        ? theme.palette.warning.main
        : theme.palette.error.main

  const severityMap: Record<
    ChatAnalyze.FraudFlag["severity"],
    "error" | "warning" | "info"
  > = {
    danger: "error",
    warn: "warning",
    info: "info",
  }

  const verdictSeverityMap: Record<
    ChatAnalyze.Verdict["type"],
    "success" | "warning" | "error"
  > = {
    success: "success",
    warning: "warning",
    danger: "error",
  }

  return (
    <Stack spacing={2}>
      {/* Gauge */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center" }}>
          <GaugeChart value={fraudScore} color={gaugeColor} />
          <Typography variant="caption" color="text.secondary">
            чем меньше — тем лучше
          </Typography>
        </Box>
      </Box>

      {/* Флаги */}
      {fraudFlags.length === 0 ? (
        <Alert severity="success">
          ✅ Явных признаков накрутки не обнаружено
        </Alert>
      ) : (
        <Stack spacing={1}>
          {fraudFlags.map((flag, i) => (
            <Alert
              key={i}
              severity={severityMap[flag.severity]}
              sx={{ py: 0.5 }}
              action={
                <Chip
                  label={`+${flag.pts}`}
                  size="small"
                  variant="outlined"
                  color={severityMap[flag.severity]}
                  sx={{ fontSize: "0.7rem", height: 20 }}
                />
              }
            >
              {flag.label}
            </Alert>
          ))}
        </Stack>
      )}

      {/* Вердикт */}
      <Alert severity={verdictSeverityMap[verdict.type]} sx={{ mt: 1 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>{verdict.label}</AlertTitle>
        {verdict.recommendation}
      </Alert>
    </Stack>
  )
}
